<template>
  <FormGroup :startsEnabled="!!initialUrl || null" :scope="scope">
    <template v-slot="{ inputName, inputId, error }">

      <div v-if="imageUrl && !hidePreview" class="d-flex p-2 mb-2 border rounded" :class="{ 'flex-row-reverse': jumbotronPreviewLayout?.direction === 'rtl' }">
        <img :src="imageUrl" class="img-fluid" :style="previewStyle.image" :width="width" :height="height">
        <div v-if="!!jumbotronPreviewLayout" class="d-flex align-items-center justify-content-center ms-3" :style="previewStyle.filler">
          {{ `${$t("backend.candidacy.components.jumbotron.main_text")}...` }}
        </div>
      </div>

      <div class="input-group">
        <input
            type="file"
            ref="fileInput"
            :id="inputId"
            @change="handleFileChange"
            class="form-control"
            :class="{
              'input-changed': changed,
              'is-invalid': error,
            }"
            :name="inputName"
            accept="image/jpeg, image/png, image/webp, image/gif"
        >
        <input v-if="deleteImage" type="hidden" :name="scopeToName([...replaceScope, 'remove_image'])" :value="deleteImage">
        <button :disabled="!deleteImage && !imageUrl" v-if='!deleteImage' @click="removeImage" type="button" class='btn btn-danger'>
          <span class='fas fa-trash'/>
        </button>
        <button v-else @click="handleFileChange" type="button" class='btn btn-primary'>
          <span class='fas fa-undo'/>
        </button>
      </div>
    </template>
  </FormGroup>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { PropType, LayoutOrientation, LayoutProportions } from "@/types";
import FormGroup from "@/components/backend/candidacy/form_builder/form_items/shared/FormGroup.vue";
import { scopeToName } from '@/lib/candidacy/form_builder/utils';

interface JumbotronImagePreview {
  direction: LayoutOrientation
  layout: LayoutProportions
}

export default defineComponent({
  name: "ImageFileField",
  emits: ["update:imageUrl"],
  components: { FormGroup },
  props: {
    imageUrl: {
      type: String,
      default: "",
    },
    scope: {
      type: Array as PropType<string[]>,
      required: true,
    },
    hidePreview: {
      type: Boolean,
      default: false,
    },
    jumbotronPreviewLayout: {
      type: Object as PropType<JumbotronImagePreview>,
      default: null,
    },
    width: {
      type: String,
      default: ""
    },
    height: {
      type: String,
      default: ""
    }
  },
  data({imageUrl}) {
    return {
      initialUrl: imageUrl,
      deleteImage: false,
      replaceScope: '',
    }
  },
  computed: {
    changed() {
      return this.imageUrl != this.initialUrl;
    },
    previewStyle() {
      if (!this.jumbotronPreviewLayout) return {
        image: ``,
        filler: ``,
      };
      return {
        image: this.deleteImage ? `width: ${this.jumbotronPreviewLayout.layout[0]}%; opacity: .5` : `width: ${this.jumbotronPreviewLayout.layout[0]}%;`,
        filler: `width: ${this.jumbotronPreviewLayout.layout[1]}%; border: dashed 1px var(--bs-border-color); color: var(--bs-border-color);`,
      };
    },
  },
  methods: {
    scopeToName,
    async fetchImageFromUrl(url: string) {
      const fileName = new URL(url).pathname.split("/").slice(-1)[0];
      const response = await fetch(url);
      return new File([await response.blob()], fileName);
    },
    handleFileChange(_event: Event) {
      this.previewStyle.image = `width: ${this.jumbotronPreviewLayout?.layout[0]}%; opacity: 1`
      this.deleteImage = false
      const newImageUrl = URL.createObjectURL(this.$refs?.fileInput?.files[0]);
      this.$emit("update:imageUrl", newImageUrl);
    },
    removeImage() {
      this.replaceScope = this.scope.slice(0, this.scope.length-1);
      this.deleteImage = true
      this.previewStyle.image = `width: ${this.jumbotronPreviewLayout?.layout[0]}%; opacity: .5`
    },
  },
});
</script>
